import {inject, singleton} from "tsyringe";
import {CommandHandler} from "../../decorators/command-handler.decorator";
import {IssueType} from "../../domain/entities/issue-type.entity";
import {MessageBroker} from "../../message-broker/message-broker";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {CommandBus} from "../../services/command-bus.service";
import {UpdateIssueTypeCommand} from "../update-issue-type.command";
import {ICommandHandler} from "./i-command.handler";
import {ProxyService} from "../../advanced-form/proxy.service";
import {FieldProxy} from "../../advanced-form/field.proxy";
import {IssueTypeRepository} from "../../domain/repositories/issue-type.repository";
import {takeOverRootFlags} from "../../domain/helpers/issue-type-root.helper";

@CommandHandler(UpdateIssueTypeCommand)
@singleton()
export class UpdateIssueTypeCommandHandler implements ICommandHandler<IssueType> {

    constructor(
        @inject(CommandBus) private readonly _commandBus: CommandBus,
        @inject(MessageBroker) private readonly _messageBroker: MessageBroker,
        @inject(ProxyService) private readonly _proxyService: ProxyService<IssueType>,
        @inject(IssueTypeRepository) private readonly _issueTypeRepository: IssueTypeRepository,
    ) {
    }

    /**
     * Sends the update; on success publishes the change and marks the type persisted. When the
     * backend refuses it (for example a move of a type that issues name to another Gruppierung),
     * the entity is rolled back, canMoveRoot is read again, and then the Gruppierung field of the
     * dialog's form is reset — the dialog shows and saves its form, not the entity. The order
     * matters: the field reset is what re-renders the dialog, so the fresh canMoveRoot must be there
     * by then to lock the picker. A failed read is ignored, so the reset always happens. Other
     * unsaved field edits stay in the form.
     */
    public async handle(command: UpdateIssueTypeCommand): Promise<void> {
        const issueType = command.payload;
        if (!(await this._commandBus.addSendCommand(command))) {
            issueType.reset();
            await this._issueTypeRepository.refreshPermissions(issueType).catch(() => undefined);
            const form = await this._proxyService.get(issueType.uid);
            (form?.getField(`parentUid`) as FieldProxy<string | null> | null)?.reset();
            return;
        }
        this._messageBroker.publish<EntityChangedPayload<IssueType>>({
            type: `entity.changed`,
            identityHash: issueType.uid,
            payload: {
                uid: issueType.uid,
                prototype: IssueType,
                changeSet: issueType.changeSet,
            }
        });
        issueType.markAsPersisted();
        // Only after an accepted save: a refused move must not take over the refused root's flags.
        await takeOverRootFlags(issueType, uid => this._issueTypeRepository.findOneById(uid));
    }

}